import validateEmail from "@core/utils/validateEmail";
import PageDataContextService from "@core-ui/ContextServices/PageDataContext";
import SourceDataService from "@core-ui/ContextServices/SourceDataService";
import EnterpriseApi from "@ext/enterprise/EnterpriseApi";
import { withTypedEmail } from "@ext/enterprise/notifications/withTypedEmail";
import { getEnterpriseSourceData } from "@ext/enterpriseCommon/logic/storage/getEnterpriseSourceData";
import t from "@ext/localization/locale/translate";
import { Level, traced } from "@ext/loggers/opentelemetry";
import { MultiSelect } from "@ui-kit/MultiSelect";
import type { SearchSelectOption } from "@ui-kit/SearchSelect";
import { TagInput } from "@ui-kit/TagInput";
import { useCallback, useEffect, useState } from "react";

interface NotificationUsersFieldProps {
	value: SearchSelectOption[];
	onChange: (value: SearchSelectOption[]) => void;
}

const isNewEmail = (email: string, emails: string[]) =>
	validateEmail(email) && !emails.some((e) => e.toLowerCase() === email.toLowerCase());

const NotificationUsersField = ({ value, onChange }: NotificationUsersFieldProps) => {
	const gesUrl = PageDataContextService.value.conf.enterprise?.gesUrl;
	const token = getEnterpriseSourceData(SourceDataService.value, gesUrl)?.token;
	const [enterpriseApi] = useState(() => new EnterpriseApi(gesUrl));
	const [isEnabledGetUsers, setIsEnabledGetUsers] = useState(false);

	useEffect(() => {
		enterpriseApi
			.isEnabledGetUsers()
			.then(setIsEnabledGetUsers)
			.catch(() => setIsEnabledGetUsers(false));
	}, [enterpriseApi]);

	const loadUserOptions = useCallback(
		async ({ searchQuery }: { searchQuery: string }) => {
			if (!searchQuery?.trim()) return { options: [] };
			if (!token) return { options: withTypedEmail(searchQuery, []) };
			try {
				const users = await traced("NotificationSettingsModal.loadUserOptions", { level: Level.Internal }, () =>
					enterpriseApi.getUsers(searchQuery, token),
				);
				return {
					options: withTypedEmail(
						searchQuery,
						users.map((user) => ({
							value: user.email,
							label: `${user.email}${user.name ? ` (${user.name})` : ""}`.trim(),
						})),
					),
				};
			} catch {
				return { options: withTypedEmail(searchQuery, []) };
			}
		},
		[token, enterpriseApi],
	);

	if (!isEnabledGetUsers) {
		return (
			<TagInput
				onChange={(emails) => onChange(emails.map((email) => ({ value: email, label: email })))}
				placeholder={t("email-input-hint")}
				validateTag={(email, emails, index) =>
					isNewEmail(
						email,
						emails.filter((_, i) => i !== index),
					)
				}
				value={value.map((user) => String(user.value))}
			/>
		);
	}

	return (
		<MultiSelect
			emptyText={t("enterprise.admin.users.users-not-found")}
			errorText={t("enterprise.admin.search-error")}
			loadMode="input"
			loadOptions={loadUserOptions}
			minInputLength={1}
			onChange={onChange}
			placeholder={t("notifications.mailing-list-search-users")}
			searchPlaceholder={t("find2")}
			value={value}
		/>
	);
};

export default NotificationUsersField;
